import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiMinus, FiPlus, FiTrash2, FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useShoppingCard } from '../../../Context/ShoppingCardContext';
import CommandeService from '../../../service/CommandeService';
import { lockBodyScroll, unlockBodyScroll } from '../../../utils/bodyScrollLock';
import CartIcon from './CartIcon';
import './ButtonPanier.css';

const ButtonPanier = () => {
  const {
    cardItems,
    increaseCardQuantity,
    decreaseCardQuantity,
    removeFromCard,
    setCardItems
  } = useShoppingCard();

  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [numero, setNumero] = useState('');
  const [adresse, setAdresse] = useState('');
  const [remarque, setRemarque] = useState('');

  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  useEffect(() => {
    if (!show) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [show]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setShow(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  const envoyerCommande = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const data = {
      nom,
      prenom,
      numero,
      remarque,
      adresse,
      article: cardItems,
    };

    try {
      await CommandeService.ajouter(data);
      toast.success('Commande envoyée !');
      setNom('');
      setPrenom('');
      setNumero('');
      setAdresse('');
      setRemarque('');
      setCardItems([]);
      handleClose();
    } catch (err) {
      toast.error("Erreur lors de l'envoi !");
    } finally {
      setSubmitting(false);
    }
  };

  const total = cardItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const totalArticles = cardItems.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <>
      <Toaster />

      <button type="button" className="hd-nav-icon-btn cart-button" onClick={handleShow} aria-label="Panier">
        <CartIcon size={20} bump={cardItems.length} />
        <AnimatePresence>
          {cardItems.length > 0 && (
            <motion.span
              key={cardItems.length}
              className="cart-badge"
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.4, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 500, damping: 20 }}
            >
              {cardItems.length}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {createPortal(
        <AnimatePresence>
          {show && (
            <motion.div
              key="cart-backdrop"
              className="hd-cart-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
            />
          )}
          {show && (
            <motion.div
              key="cart-drawer"
              className="hd-cart-drawer"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <form onSubmit={envoyerCommande} className="hd-cart-form">
                <div className="hd-cart-head">
                  <div className="hd-cart-head-title">
                    <span className="hd-cart-head-icon"><CartIcon size={19} bump={cardItems.length} /></span>
                    <span>Mon panier</span>
                    {cardItems.length > 0 && <span className="hd-cart-head-count">{totalArticles}</span>}
                  </div>
                  <button type="button" className="hd-cart-close" onClick={handleClose} aria-label="Fermer le panier">
                    <FiX />
                  </button>
                </div>

                <div className="hd-cart-body">
                  {cardItems.length === 0 ? (
                    <div className="hd-cart-empty">
                      <motion.div
                        className="hd-cart-empty-icon"
                        animate={{ y: [0, -6, 0] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <FiShoppingBag size={30} />
                      </motion.div>
                      <p className="hd-cart-empty-title">Votre panier est vide</p>
                      <p className="hd-cart-empty-sub">Les articles ajoutés apparaîtront ici.</p>
                      <button type="button" className="hd-cart-empty-btn" onClick={handleClose}>
                        Continuer mes achats
                      </button>
                    </div>
                  ) : (
                    <>
                      <ul className="hd-cart-items">
                        <AnimatePresence>
                          {cardItems.map((item) => (
                            <motion.li
                              key={item.id + item.dimension}
                              className="hd-cart-item"
                              initial={{ opacity: 0, x: 16 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 16, height: 0, marginBottom: 0, paddingBottom: 0 }}
                              transition={{ duration: 0.25 }}
                              layout
                            >
                              <div className="hd-cart-item-media">
                                <img src={item.image} alt={item.titre} />
                              </div>

                              <div className="hd-cart-item-info">
                                <span className="hd-cart-item-title">{item.titre}</span>
                                {item.dimension2 && <span className="hd-cart-item-dim">{item.dimension2}</span>}

                                <div className="hd-cart-item-row">
                                  <div className="hd-cart-qty">
                                    <button
                                      type="button"
                                      className="hd-cart-qty-btn"
                                      onClick={() => decreaseCardQuantity(item.id, item.dimension)}
                                      aria-label="Diminuer la quantité"
                                    >
                                      <FiMinus size={12} />
                                    </button>
                                    <span className="hd-cart-qty-value">{item.quantity}</span>
                                    <button
                                      type="button"
                                      className="hd-cart-qty-btn"
                                      onClick={() => increaseCardQuantity(item.id, item.dimension)}
                                      aria-label="Augmenter la quantité"
                                    >
                                      <FiPlus size={12} />
                                    </button>
                                  </div>

                                  <span className="hd-cart-item-price">{(item.price * item.quantity).toFixed(2)} DT</span>
                                </div>
                              </div>

                              <button
                                type="button"
                                className="hd-cart-item-remove"
                                onClick={() => removeFromCard(item.id, item.dimension)}
                                aria-label="Retirer l'article"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </motion.li>
                          ))}
                        </AnimatePresence>
                      </ul>

                      <div className="hd-cart-summary">
                        <div className="hd-cart-summary-row">
                          <span>Sous-total ({totalArticles} article{totalArticles > 1 ? 's' : ''})</span>
                          <span>{total.toFixed(2)} DT</span>
                        </div>
                      </div>

                      <div className="hd-cart-section-heading">
                        <span>Coordonnées de livraison</span>
                      </div>

                      <div className="hd-cart-fields">
                        <div className="hd-cart-fields-row">
                          <label className="hd-cart-field">
                            <span>Nom</span>
                            <input value={nom} onChange={(e) => setNom(e.target.value)} required />
                          </label>
                          <label className="hd-cart-field">
                            <span>Prénom</span>
                            <input value={prenom} onChange={(e) => setPrenom(e.target.value)} required />
                          </label>
                        </div>

                        <label className="hd-cart-field">
                          <span>Adresse</span>
                          <input value={adresse} onChange={(e) => setAdresse(e.target.value)} required />
                        </label>

                        <label className="hd-cart-field">
                          <span>Téléphone</span>
                          <PhoneInput
                            defaultCountry="TN"
                            value={numero}
                            onChange={setNumero}
                            className="hd-cart-phone"
                          />
                        </label>

                        <label className="hd-cart-field">
                          <span>Remarque <em>(optionnel)</em></span>
                          <textarea
                            rows={2}
                            value={remarque}
                            onChange={(e) => setRemarque(e.target.value)}
                          />
                        </label>
                      </div>
                    </>
                  )}
                </div>

                {cardItems.length > 0 && (
                  <div className="hd-cart-footer">
                    <div className="hd-cart-footer-total">
                      <span>Total</span>
                      <strong>{total.toFixed(2)} DT</strong>
                    </div>
                    <motion.button
                      type="submit"
                      className="hd-cart-submit"
                      disabled={submitting}
                      whileHover={{ scale: submitting ? 1 : 1.02 }}
                      whileTap={{ scale: submitting ? 1 : 0.98 }}
                    >
                      {submitting ? 'Envoi…' : (
                        <>
                          Valider la commande
                          <FiArrowRight size={16} />
                        </>
                      )}
                    </motion.button>
                  </div>
                )}
              </form>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default ButtonPanier;
